import { useCallback } from 'react';

import { useAnalyticalVariableUsageQuery } from '../api/useAnalyticalVariableUsageQuery';
import type { AnalyticalVariableUsageItem } from '../api/useAnalyticalVariableUsageQuery';
import { useDeleteAnalyticalVariableMutation } from '../api/useDeleteAnalyticalVariableMutation';

interface UseDeleteAnalyticalVariableDialogArgs {
  analyticalVariableId: string;
  onClose: () => void;
  /** Project the delete was triggered from, when known. Omit for the library page. */
  projectId?: string;
}

export interface UseDeleteAnalyticalVariableDialogResult {
  onSubmit: () => Promise<void>;
  closeDialog: () => void;
  /** Something still uses the variable; Delete is unavailable and ``usages`` says what. */
  isInUse: boolean;
  /**
   * The usage read failed, so nothing is known about what uses the variable.
   * Distinct from ``isInUse: false``, which is an answer.
   */
  usageCheckFailed: boolean;
  /** Re-run the usage read after a failure. */
  retryUsageCheck: () => void;
  usages: AnalyticalVariableUsageItem[];
  /** The variable has an open edit that goes with it. Copy only, never a gate. */
  editInProgress: boolean;
  isCheckingUsage: boolean;
  isPending: boolean;
  submittingError: { message: string } | undefined;
  isValidToSubmit: boolean;
}

/**
 * Behaviour for the delete-variable confirmation (OR-6234). On open it asks the
 * server what still uses the variable so the dialog can explain a refusal
 * before the researcher clicks (AC 3); the server runs the same check again
 * before writing, so this read is for the copy, not the guard. Intl-free so the
 * view owns copy. Shared by the project artifact list and the library page.
 */
export const useDeleteAnalyticalVariableDialog = ({
  analyticalVariableId,
  onClose,
  projectId,
}: UseDeleteAnalyticalVariableDialogArgs): UseDeleteAnalyticalVariableDialogResult => {
  const usageQuery = useAnalyticalVariableUsageQuery(analyticalVariableId);
  const { mutateAsync, isPending, error, reset } = useDeleteAnalyticalVariableMutation();

  const usage = usageQuery.data?.data;
  const isInUse = usage?.isUsed ?? false;
  const isCheckingUsage = usageQuery.isLoading;
  const usageCheckFailed = usageQuery.isError;

  const retryUsageCheck = useCallback(() => {
    void usageQuery.refetch();
  }, [usageQuery]);

  const onSubmit = useCallback(async () => {
    if (isInUse) return;
    await mutateAsync({ analyticalVariableId, projectId });
  }, [isInUse, mutateAsync, analyticalVariableId, projectId]);

  const closeDialog = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return {
    onSubmit,
    closeDialog,
    isInUse,
    usageCheckFailed,
    retryUsageCheck,
    usages: usage?.usages ?? [],
    editInProgress: usage?.editInProgress ?? false,
    isCheckingUsage,
    isPending,
    submittingError: error ? { message: error.message } : undefined,
    // A failed usage read must not read as "not in use": refuse to submit until it
    // answers. The view says so in ``usageCheckFailed`` copy rather than leaving a
    // dead button under confirm copy.
    isValidToSubmit: !isCheckingUsage && !usageCheckFailed && !isInUse && !isPending,
  };
};
